import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import ListNotes from "../noteList/ListNotes";
import FullTutorialTrigger from "../Tutorial/FullTutorialTrigger";
import AddPatientModal from "../noteList/AddPatientModal";
import Sidebar from "../Sidebar";
import Calendar from "../schedule/Calendar";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import jwt_decode from "jwt-decode";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import userJSON from "../../utils/userJSON";
import sendServerLogging from "../../utils/sendServerLogging";
import { identifyUser, trackEvent, Events } from "../../lib/analytics";
import { logInfo } from "../../lib/sentryLogging";
import { useFeatureGate } from "../../hooks/useFeatureGate";
import useDashboardSummary from "../../hooks/useDashboardSummary";
import useReviewCount from "../../hooks/useReviewCount";
import useDashboardUserInfo from "../../hooks/useDashboardUserInfo";
import {
  useProviderAttestationStatus,
  useUpdateProviderAttestation,
} from "../../hooks/useProviderAttestation";
import { sharedMacrosKeys } from "../../hooks/useSharedMacros";
import useNoteTitlesPull from "../../hooks/useNoteTitlesPull";
import useRefreshSchedule from "../../hooks/useRefreshSchedule";
import useIsInitialLoad from "../../hooks/useIsInitialLoad";
import useFocusWhenReady from "../../hooks/useFocusWhenReady";
import useInitialSchedulePull from "../../hooks/useInitialSchedulePull";
import { isStandaloneUser as getIsStandaloneUser } from "../../utils/standaloneUser";
import { api } from "../../lib/api";
import SpecialtySwitcherModal from "./SpecialtySwitcherModal";
import ProviderContextPanel from "./ProviderContextPanel";
import ProviderAttestationModal from "./ProviderAttestationModal";
import {
  SPECIALTY_OPTIONS,
  SAN_MATEO_SPECIALTY_OPTIONS,
  resolveSpecialtyToDbValue,
  getSpecialtyLabelFromDbValue,
} from "../../utils/specialties";
import "../schedule/style.css";
import { getSessionToken } from "../../lib/sessionToken";

const EMPTY_NOTE_TITLE_ENTRIES = [];

//Dashboard is the Home Page where doctors can view/add patients to a schedule, view/edit notes, and delete patients.
const Dashboard = ({ setAuth }) => {
  const isStandaloneUser = getIsStandaloneUser();
  const location = useLocation();
  const queryClient = useQueryClient();
  const previousLocationRef = useRef(location.pathname);
  const refreshRequestedOnLoadRef = useRef(
    new URLSearchParams(window.location.search).get("refresh") === "true"
  );
  const hasRemovedRefreshParamRef = useRef(false);
  const hasRunSchedulePullRef = useRef(false);
  const isEditingRef = useRef(false);

  // Function to format the date in the desired format
  const formatDate = (date) => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const getInitialPickedDate = () => {
    try {
      // check to see if refresh query parameter is set -- if so, return the current date
      const searchParams = new URLSearchParams(window.location.search);
      const shouldRefresh = searchParams.get("refresh") === "true";
      if (shouldRefresh) {
        return formatDate(new Date());
      }
      // otherwise, return the stored date
      const storedDate = window.localStorage.getItem("date");
      if (!storedDate) {
        return formatDate(new Date());
      }

      return storedDate;
    } catch {
      return formatDate(new Date());
    }
  };
  const [name, setName] = useState(
    localStorage.getItem("userName") ? localStorage.getItem("userName") : ""
  );
  const [turnaroundTime, setTurnaroundTime] = useState("");
  const [allPatients, setAllPatients] = useState([]);
  const [patientsChange, setPatientsChange] = useState(false);
  const [pickedDate, setPickedDate] = useState(getInitialPickedDate);
  const [org, setOrg] = useState("");
  const [superOrg, setSuperOrg] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [specialty, setSpecialty] = useState(null);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [submittingPatientId, setSubmittingPatientId] = useState(null);
  const [noteTitlesFavorites, setNoteTitlesFavorites] = useState([]);
  const multiSpecialtyGate = useFeatureGate("multi-specialty");
  const sanMateoSpecialtyGate = useFeatureGate("san-mateo-specialties");
  const vaSiteSwapGate = useFeatureGate("va-site-swap");
  const isNoteTitlesEnabled = true;
  const hasVaSiteSwapAccess = vaSiteSwapGate.hasFeature("on");
  const isVaSiteSwapReady = !vaSiteSwapGate.isLoading;
  const hasMultiSpecialtyAccess = multiSpecialtyGate.hasFeature("on");
  const hasSanMateoSpecialtyAccess = sanMateoSpecialtyGate.hasFeature("on");
  const hasSpecialtySwitcherAccess =
    isStandaloneUser || hasMultiSpecialtyAccess || hasSanMateoSpecialtyAccess;
  const isSpecialtySwitcherReady =
    isStandaloneUser || (!multiSpecialtyGate.isLoading && !sanMateoSpecialtyGate.isLoading);
  const activeSpecialtyOptions = useMemo(
    () => (hasSanMateoSpecialtyAccess ? SAN_MATEO_SPECIALTY_OPTIONS : SPECIALTY_OPTIONS),
    [hasSanMateoSpecialtyAccess]
  );
  const [showSpecialtyModal, setShowSpecialtyModal] = useState(false);
  const [isSavingSpecialty, setIsSavingSpecialty] = useState(false);
  const noteTitlesPullParams = useMemo(() => {
    if (isStandaloneUser) {
      return null;
    }

    const sta3n = localStorage.getItem("va_sta3n");
    const baseUrl = localStorage.getItem("va_fhir_server");
    const token = getSessionToken();
    if (!sta3n || !baseUrl || !token) {
      return null;
    }

    try {
      const decoded = jwt_decode(token);
      const userId = decoded?.user?.id;
      if (!userId) {
        return null;
      }

      return {
        userId,
        baseUrl,
        sta3n,
        todaysDate: new Date().toISOString().split("T")[0],
      };
    } catch (error) {
      console.warn(
        "Failed to decode token for note titles pull:",
        error?.message || "unknown error"
      );
      return null;
    }
  }, [isStandaloneUser]);

  // --- React Query hooks ---

  const dashboardSummaryQuery = useDashboardSummary({
    pickedDate,
    refetchInterval: isEditing ? false : 5000,
  });

  const { refetch: refetchDashboardSummary } = dashboardSummaryQuery;
  useEffect(() => {
    const wasEditing = isEditingRef.current;
    isEditingRef.current = isEditing;
    if (wasEditing && !isEditing) {
      refetchDashboardSummary().then(({ data }) => {
        if (data && !isEditingRef.current) {
          setAllPatients(data);
        }
      });
    }
  }, [isEditing, refetchDashboardSummary]);

  // Child components call setPatientsChange(true) after writing status changes.
  useEffect(() => {
    if (patientsChange) {
      refetchDashboardSummary();
      setPatientsChange(false);
    }
  }, [patientsChange, refetchDashboardSummary]);

  const reviewCountQuery = useReviewCount();
  const numNotesWithReview = reviewCountQuery.data ?? null;

  const dashboardUserInfoQuery = useDashboardUserInfo();
  const providerAttestationStatusQuery = useProviderAttestationStatus(
    dashboardUserInfoQuery.isSuccess
  );
  const updateProviderAttestationMutation = useUpdateProviderAttestation();
  const noteTitlesPullQuery = useNoteTitlesPull(noteTitlesPullParams);
  const noteTitlesEntries = noteTitlesPullQuery.data?.entries ?? EMPTY_NOTE_TITLE_ENTRIES;
  const refreshScheduleMutation = useRefreshSchedule();
  const initialSchedulePullMutation = useInitialSchedulePull();

  useEffect(() => {
    if (isStandaloneUser) {
      setNoteTitlesFavorites([]);
      return;
    }

    if (noteTitlesPullQuery.data) {
      setNoteTitlesFavorites(noteTitlesPullQuery.data.favorites);
    }
  }, [isStandaloneUser, noteTitlesPullQuery.data]);

  useEffect(() => {
    if (noteTitlesPullQuery.isError) {
      console.warn(
        "Note titles pull error:",
        noteTitlesPullQuery.error?.message || "unknown error"
      );
    }
  }, [noteTitlesPullQuery.error, noteTitlesPullQuery.isError]);
  // Sync dashboard summary data into local state
  useEffect(() => {
    const parseRes = dashboardSummaryQuery.data;
    if (!parseRes) return;

    const hasPatientWithName = parseRes.length > 0 && parseRes[0]?.user_name;
    if (hasPatientWithName) {
      const {
        user_name,
        turnaround_time,
        organization,
        super_org,
        specialty: specialtyVal,
      } = parseRes[0];

      setName(user_name);
      setTurnaroundTime(turnaround_time);
      setOrg(organization);
      setSuperOrg(super_org);
      const normalizedSpecialty = resolveSpecialtyToDbValue(specialtyVal, activeSpecialtyOptions);
      setSpecialty(normalizedSpecialty || specialtyVal);

      identifyUser({
        name: user_name,
        org: organization,
        super_org: super_org,
        specialty: specialtyVal,
      });
    }

    if (!isEditingRef.current) {
      setAllPatients(parseRes);
    }
  }, [dashboardSummaryQuery.data, activeSpecialtyOptions]);

  // Persist picked date
  useEffect(() => {
    window.localStorage.setItem("date", pickedDate);
  }, [pickedDate]);

  useEffect(() => {
    const userInfo = dashboardUserInfoQuery.data;
    if (!userInfo) return;

    if (userInfo.user_name) {
      setName((previousName) => previousName || userInfo.user_name);
    }

    if (userInfo.organization) {
      setOrg((previousOrg) => previousOrg || userInfo.organization);
    }

    if (userInfo.super_org) {
      setSuperOrg((previousSuperOrg) => previousSuperOrg || userInfo.super_org);
    }

    if (userInfo.specialty) {
      const normalizedSpecialty = resolveSpecialtyToDbValue(
        userInfo.specialty,
        activeSpecialtyOptions
      );
      setSpecialty(
        (previousSpecialty) => previousSpecialty || normalizedSpecialty || userInfo.specialty
      );
    }
  }, [dashboardUserInfoQuery.data, activeSpecialtyOptions]);

  const sortedPatients = useMemo(() => {
    if (!allPatients || allPatients.length === 0) {
      return [];
    }

    return [...allPatients].sort((a, b) => {
      const aHasApptTime = a.appt_time && a.appt_time.trim() !== "";
      const bHasApptTime = b.appt_time && b.appt_time.trim() !== "";

      if (aHasApptTime && bHasApptTime) {
        return a.appt_time.localeCompare(b.appt_time);
      }

      if (aHasApptTime && !bHasApptTime) {
        return -1;
      }

      if (!aHasApptTime && bHasApptTime) {
        return 1;
      }

      return a.patient_id - b.patient_id;
    });
  }, [allPatients]);
  const noteTitleOptionValues = useMemo(
    () =>
      (Array.isArray(noteTitlesEntries) ? noteTitlesEntries : [])
        .map((entry) => entry?.ien || entry?.title)
        .filter(Boolean),
    [noteTitlesEntries]
  );
  const noteTitleOptionValueSet = useMemo(
    () => new Set(noteTitleOptionValues),
    [noteTitleOptionValues]
  );
  const firstNoteTitleValue = noteTitleOptionValues[0] || "";

  const formatDateForApi = (dateString) => {
    if (!dateString) return null;
    const parsedDate = new Date(dateString);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }
    return parsedDate.toISOString().split("T")[0];
  };

  const getLocalIsoDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getReadableErrorMessage = (error, fallbackMessage) => {
    const messageFromError =
      typeof error?.message === "string" && error.message !== "[object Object]"
        ? error.message
        : null;

    const data = error?.data;
    const messageFromData =
      data && typeof data === "object" ? data.message || data.error || data.details : null;

    if (typeof messageFromError === "string" && messageFromError.trim()) {
      return messageFromError;
    }

    if (typeof messageFromData === "string" && messageFromData.trim()) {
      return messageFromData;
    }

    if (messageFromData) {
      try {
        return JSON.stringify(messageFromData);
      } catch {
        return fallbackMessage;
      }
    }

    return fallbackMessage;
  };

  const handleProviderAttestationSubmit = useCallback(async () => {
    try {
      await updateProviderAttestationMutation.mutateAsync({
        is_completed: true,
        date_completed: getLocalIsoDate(),
      });
      toast.success("Welcome to Knowtex!");
    } catch (error) {
      toast.error(getReadableErrorMessage(error, "Failed to save provider attestation."));
    }
  }, [updateProviderAttestationMutation]);

  const handleProviderAttestationRetry = useCallback(() => {
    providerAttestationStatusQuery.refetch();
  }, [providerAttestationStatusQuery]);

  const handleRefreshSchedule = useCallback(async () => {
    if (isStandaloneUser) {
      toast.info("Schedule refresh is disabled for standalone users.");
      return;
    }

    if (isRefreshing) {
      toast.info("Schedule refresh already in progress");
      return;
    }

    const dateForApi = formatDateForApi(pickedDate);
    const fhirUrl = localStorage.getItem("va_fhir_server");

    if (!fhirUrl) {
      toast.error("VA FHIR server not configured. Please authenticate with VA.");
      return;
    }

    setIsRefreshing(true);

    try {
      toast.info("Refreshing schedule from VA...", { autoClose: 2000 });

      const data = await refreshScheduleMutation.mutateAsync({
        fhirUrl,
        specificDate: dateForApi,
        sta3n: localStorage.getItem("va_sta3n"),
      });
      const scheduledElsewhere = Number(data.appointments_scheduled_elsewhere) || 0;
      const cancelledAtCurrentFacility = Number.isFinite(
        Number(data.appointments_cancelled_at_current_facility)
      )
        ? Number(data.appointments_cancelled_at_current_facility)
        : Math.max((Number(data.appointments_cancelled) || 0) - scheduledElsewhere, 0);

      console.log("Schedule Refresh Summary:", {
        date: dateForApi,
        appointments_found: data.appointments_found,
        new_patients_added: data.new_patients_added,
        patients_reactivated: data.patients_reactivated,
        appointments_cancelled: data.appointments_cancelled,
        appointments_scheduled_elsewhere: scheduledElsewhere,
        appointments_cancelled_at_current_facility: cancelledAtCurrentFacility,
        appointments_skipped: data.appointments_skipped,
        processing_time: data.processing_time,
      });

      const parts = [];

      if (data.new_patients_added > 0) {
        parts.push(
          `${data.new_patients_added} new patient${data.new_patients_added !== 1 ? "s" : ""} added`
        );
      }

      if (data.patients_reactivated > 0) {
        parts.push(
          `${data.patients_reactivated} patient${data.patients_reactivated !== 1 ? "s" : ""} reactivated`
        );
      }

      if (scheduledElsewhere > 0) {
        parts.push(
          `${scheduledElsewhere} appointment${
            scheduledElsewhere !== 1 ? "s" : ""
          } scheduled elsewhere`
        );
      }

      if (cancelledAtCurrentFacility > 0) {
        parts.push(
          `${cancelledAtCurrentFacility} appointment${
            cancelledAtCurrentFacility !== 1 ? "s" : ""
          } cancelled`
        );
      }

      if (data.appointments_found === 0) {
        toast.info(
          `No appointments found for ${dateForApi}${
            parts.length > 0 ? `: ${parts.join(", ")}` : ""
          }`,
          { autoClose: parts.length > 0 ? 6000 : 4000 }
        );
      } else {
        if (parts.length > 0) {
          toast.success(
            `Found ${data.appointments_found} appointment${data.appointments_found !== 1 ? "s" : ""}: ${parts.join(", ")}`,
            { autoClose: 6000 }
          );
        } else {
          toast.info(
            `Found ${data.appointments_found} appointment${data.appointments_found !== 1 ? "s" : ""} (all previously imported)`,
            { autoClose: 4000 }
          );
        }
      }

      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "SCHEDULE_REFRESH",
          eventData: {
            date: dateForApi,
            appointments_found: data.appointments_found,
            new_patients_added: data.new_patients_added,
            patients_reactivated: data.patients_reactivated,
            appointments_cancelled: data.appointments_cancelled,
            appointments_scheduled_elsewhere: scheduledElsewhere,
            appointments_cancelled_at_current_facility: cancelledAtCurrentFacility,
            appointments_skipped: data.appointments_skipped,
            processing_time: data.processing_time,
          },
        },
      });
      logInfo("Schedule refreshed", "schedule.refreshed", {
        date: dateForApi,
        appointments_found: data.appointments_found,
        new_patients_added: data.new_patients_added,
        patients_reactivated: data.patients_reactivated,
        appointments_cancelled: data.appointments_cancelled,
        appointments_scheduled_elsewhere: scheduledElsewhere,
        appointments_cancelled_at_current_facility: cancelledAtCurrentFacility,
        appointments_skipped: data.appointments_skipped,
        processing_time: data.processing_time,
      });

      setPatientsChange(true);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { logEvent: "SCHEDULE_REFRESH_ERROR" },
        extra: { date: dateForApi },
      });
      console.error("Error refreshing schedule:", err);
      const errorMessage = getReadableErrorMessage(err, "Failed to refresh schedule");
      toast.error(errorMessage);

      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "SCHEDULE_REFRESH_ERROR",
          eventData: {
            date: dateForApi,
            error: errorMessage,
          },
        },
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, pickedDate, refreshScheduleMutation, isStandaloneUser]);

  const handleInitialSchedulePull = useCallback(
    async (fhirUrl, sta3n) => {
      if (isStandaloneUser) {
        return;
      }

      setIsRefreshing(true);

      try {
        toast.info("Loading your appointments from VA...", { autoClose: 3000 });

        const data = await initialSchedulePullMutation.mutateAsync({ fhirUrl, sta3n });
        const scheduledElsewhere = Number(data.appointments_scheduled_elsewhere) || 0;
        const cancelledAtCurrentFacility = Number.isFinite(
          Number(data.appointments_cancelled_at_current_facility)
        )
          ? Number(data.appointments_cancelled_at_current_facility)
          : Math.max((Number(data.appointments_cancelled) || 0) - scheduledElsewhere, 0);

        console.log("Initial Schedule Pull Summary:", {
          appointments_found: data.appointments_found,
          new_patients_added: data.new_patients_added,
          patients_reactivated: data.patients_reactivated,
          appointments_cancelled: data.appointments_cancelled,
          appointments_scheduled_elsewhere: scheduledElsewhere,
          appointments_cancelled_at_current_facility: cancelledAtCurrentFacility,
          appointments_skipped: data.appointments_skipped,
          days_searched: data.days_searched,
          processing_time: data.processing_time,
        });

        const parts = [];

        if (data.new_patients_added > 0) {
          parts.push(
            `${data.new_patients_added} new patient${data.new_patients_added !== 1 ? "s" : ""}`
          );
        }

        if (data.patients_reactivated > 0) {
          parts.push(
            `${data.patients_reactivated} patient${data.patients_reactivated !== 1 ? "s" : ""} reactivated`
          );
        }

        if (scheduledElsewhere > 0) {
          parts.push(`${scheduledElsewhere} scheduled elsewhere`);
        }

        if (cancelledAtCurrentFacility > 0) {
          parts.push(`${cancelledAtCurrentFacility} cancelled`);
        }

        if (data.appointments_found === 0) {
          toast.info(
            `No appointments found for the next ${
              parseInt(data.days_searched) + 1
            } days${parts.length > 0 ? `: ${parts.join(", ")}` : ""}`,
            { autoClose: parts.length > 0 ? 5000 : 3000 }
          );
        } else if (parts.length > 0) {
          toast.success(
            `Welcome! Found ${data.appointments_found} appointment${data.appointments_found !== 1 ? "s" : ""}: ${parts.join(", ")}`,
            { autoClose: 5000 }
          );
        } else {
          toast.info(
            `Found ${data.appointments_found} appointment${data.appointments_found !== 1 ? "s" : ""} (all previously imported)`,
            { autoClose: 3000 }
          );
        }

        sendServerLogging({
          body: {
            ...userJSON(localStorage.getItem("userName")),
            event: "INITIAL_SCHEDULE_PULL",
            eventData: {
              appointments_found: data.appointments_found,
              new_patients_added: data.new_patients_added,
              patients_reactivated: data.patients_reactivated,
              appointments_cancelled: data.appointments_cancelled,
              appointments_scheduled_elsewhere: scheduledElsewhere,
              appointments_cancelled_at_current_facility: cancelledAtCurrentFacility,
              appointments_skipped: data.appointments_skipped,
              days_searched: data.days_searched,
              processing_time: data.processing_time,
            },
          },
        });
        logInfo("Schedule initial pull completed", "schedule.initial_pull", {
          appointments_found: data.appointments_found,
          new_patients_added: data.new_patients_added,
          patients_reactivated: data.patients_reactivated,
          appointments_cancelled: data.appointments_cancelled,
          appointments_scheduled_elsewhere: scheduledElsewhere,
          appointments_cancelled_at_current_facility: cancelledAtCurrentFacility,
          appointments_skipped: data.appointments_skipped,
          days_searched: data.days_searched,
          processing_time: data.processing_time,
        });

        setPatientsChange(true);
      } catch (err) {
        Sentry.captureException(err, {
          tags: { logEvent: "INITIAL_SCHEDULE_PULL_ERROR" },
        });
        console.error("Error during initial schedule pull:", err);
        const errorMessage = getReadableErrorMessage(err, "Failed to load appointments");
        toast.error(errorMessage);

        sendServerLogging({
          body: {
            ...userJSON(localStorage.getItem("userName")),
            event: "INITIAL_SCHEDULE_PULL_ERROR",
            eventData: {
              error: errorMessage,
            },
          },
        });
      } finally {
        setIsRefreshing(false);
      }
    },
    [initialSchedulePullMutation, isStandaloneUser]
  );

  const openSpecialtyModal = useCallback(() => {
    setShowSpecialtyModal(true);
  }, []);

  const closeSpecialtyModal = useCallback(() => {
    if (isSavingSpecialty) return;
    setShowSpecialtyModal(false);
  }, [isSavingSpecialty]);

  const handleSelectSpecialty = useCallback(
    async (nextSpecialtyRaw) => {
      if (isSavingSpecialty) return;

      const nextSpecialtyDb = resolveSpecialtyToDbValue(nextSpecialtyRaw, activeSpecialtyOptions);
      const currentSpecialtyDb = resolveSpecialtyToDbValue(specialty || "", activeSpecialtyOptions);

      if (!nextSpecialtyDb) {
        toast.error("Please select a valid specialty.");
        return;
      }

      if (nextSpecialtyDb === currentSpecialtyDb) {
        return;
      }

      setIsSavingSpecialty(true);
      try {
        const payload = await api.patch("/dashboard/specialty", {
          specialty: nextSpecialtyDb,
        });

        const updatedSpecialtyDb =
          resolveSpecialtyToDbValue(payload?.user?.specialty || "", activeSpecialtyOptions) ||
          nextSpecialtyDb;

        setSpecialty(updatedSpecialtyDb);
        setShowSpecialtyModal(false);
        queryClient.invalidateQueries({ queryKey: sharedMacrosKeys.all });

        identifyUser({
          name,
          org,
          super_org: superOrg,
          specialty: updatedSpecialtyDb,
        });

        sendServerLogging({
          body: {
            ...userJSON(localStorage.getItem("userName")),
            event: "SPECIALTY_UPDATED",
            eventData: {
              previous_specialty: currentSpecialtyDb || null,
              updated_specialty: updatedSpecialtyDb,
            },
          },
        });

        trackEvent(Events.SPECIALTY_SWAPPED, {
          previous_specialty: currentSpecialtyDb || null,
          updated_specialty: updatedSpecialtyDb,
          specialty_profile: hasSanMateoSpecialtyAccess
            ? "san-mateo-specialties"
            : "multi-specialty",
        });

        toast.success(
          `Specialty updated to ${getSpecialtyLabelFromDbValue(
            updatedSpecialtyDb,
            activeSpecialtyOptions
          )}.`
        );
      } catch (err) {
        const errorMessage = getReadableErrorMessage(err, "Failed to update specialty");
        Sentry.captureException(err, {
          tags: { flow: "specialtySwap" },
          extra: {
            previousSpecialty: specialty || null,
            requestedSpecialty: nextSpecialtyRaw || null,
            specialtyProfile: hasSanMateoSpecialtyAccess
              ? "san-mateo-specialties"
              : "multi-specialty",
          },
        });
        toast.error(errorMessage);
      } finally {
        setIsSavingSpecialty(false);
      }
    },
    [
      isSavingSpecialty,
      specialty,
      name,
      org,
      superOrg,
      activeSpecialtyOptions,
      hasSanMateoSpecialtyAccess,
      queryClient,
    ]
  );

  useEffect(() => {
    if (isStandaloneUser) {
      return;
    }

    if (!refreshRequestedOnLoadRef.current) {
      return;
    }

    if (!hasRemovedRefreshParamRef.current) {
      window.history.replaceState({}, "", "/dashboard");
      hasRemovedRefreshParamRef.current = true;
    }

    const fhirUrl = localStorage.getItem("va_fhir_server");
    const sta3n = localStorage.getItem("va_sta3n");

    if (!fhirUrl || !sta3n) {
      return;
    }

    if (!hasRunSchedulePullRef.current) {
      hasRunSchedulePullRef.current = true;
      handleInitialSchedulePull(fhirUrl, sta3n);
    }
  }, [isStandaloneUser, handleInitialSchedulePull]);

  // Refresh patient data when returning to dashboard from record page
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousLocationRef.current;

    if (previousPath.startsWith("/record") && currentPath === "/dashboard") {
      dashboardSummaryQuery.refetch();
    }

    previousLocationRef.current = currentPath;
  }, [location.pathname]);

  // Also refresh when window regains focus (user returns to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (location.pathname === "/dashboard" && !isEditing) {
        dashboardSummaryQuery.refetch();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [location.pathname, isEditing]);

  const isLoading = dashboardSummaryQuery.isLoading;
  // Only the first load replaces the page. Swapping it on date changes would
  // unmount the focused Previous/Next date button and drop keyboard focus.
  const isInitialLoad = useIsInitialLoad(isLoading, Boolean(dashboardSummaryQuery.data));
  // The greeting only renders after the first load, so route-change focus
  // management can't find it. Focusing it makes screen readers announce it.
  const greetingRef = useRef(null);
  useFocusWhenReady(greetingRef, !isInitialLoad && Boolean(name));
  const providerAttestationStatus = providerAttestationStatusQuery.data;
  const isAttestationGateLoading =
    dashboardUserInfoQuery.isSuccess &&
    providerAttestationStatusQuery.isLoading &&
    !providerAttestationStatusQuery.isSuccess;
  const shouldRequireProviderAttestation = Boolean(providerAttestationStatus?.is_required);
  const isProviderAttestationComplete = Boolean(providerAttestationStatus?.is_completed);
  const shouldShowProviderAttestationGate =
    (dashboardUserInfoQuery.isSuccess && providerAttestationStatusQuery.isError) ||
    (shouldRequireProviderAttestation && !isProviderAttestationComplete);
  const providerAttestationErrorMessage = providerAttestationStatusQuery.isError
    ? getReadableErrorMessage(
        providerAttestationStatusQuery.error,
        "Unable to verify provider attestation status."
      )
    : null;
  const specialtyDisplayName = getSpecialtyLabelFromDbValue(specialty, activeSpecialtyOptions);
  const activeSpecialtyDb =
    resolveSpecialtyToDbValue(specialty || "", activeSpecialtyOptions) ||
    activeSpecialtyOptions[0].db;

  return (
    <>
      <Helmet>
        <title>Dashboard – Knowtex</title>
      </Helmet>
      <Sidebar setAuth={setAuth} />
      <main
        id="main-content"
        tabIndex="-1"
        className="flex min-w-0 mt-5 laptop:mt-0 ml-sidebar-rail desktop:ml-sidebar-open"
      >
        {!isInitialLoad ? (
          <div
            id="dashboard-main-column"
            className="flex w-full min-w-0 flex-col transition-margin smart-chart-2b-main-column"
          >
            <h1
              id="dashboard-greeting"
              ref={greetingRef}
              tabIndex={-1}
              className="text-4xl font-bold text-accent1 mt-5 focus:outline-none smart-chart-2b-page-chrome"
            >
              Hi there, {name}
            </h1>
            {/* Fixed to the bottom-right corner, but placed here so it's the first
                Tab stop after the greeting. FeatureTutorialTrigger lives on the
                record page; rendering both overlapped two "i" icons. */}
            <FullTutorialTrigger />
            <div id="dashboard-provider-context" className="smart-chart-2b-page-chrome">
              <ProviderContextPanel
                specialtyDisplayName={specialtyDisplayName}
                canSwitchSpecialty={isSpecialtySwitcherReady && hasSpecialtySwitcherAccess}
                onSwitchSpecialty={openSpecialtyModal}
                showPointOfCare={isVaSiteSwapReady && hasVaSiteSwapAccess}
                currentSta3n={localStorage.getItem("va_sta3n") || ""}
                fhirServer={localStorage.getItem("va_fhir_server") || ""}
              />
            </div>
            <div id="dashboard-calendar" className="mt-2 mb-1 mr-3 smart-chart-2b-page-chrome">
              <Calendar
                pickedDate={pickedDate}
                setPickedDate={setPickedDate}
                formatDate={formatDate}
              />
            </div>
            <AddPatientModal
              showModal={showAddPatientModal}
              setShowModal={setShowAddPatientModal}
              setPatientsChange={setPatientsChange}
              pickedDate={pickedDate}
              specialty={specialty}
              org={org}
            />
            <SpecialtySwitcherModal
              isOpen={showSpecialtyModal}
              onClose={closeSpecialtyModal}
              currentSpecialty={specialtyDisplayName}
              activeSpecialtyDb={activeSpecialtyDb}
              onSpecialtySelect={handleSelectSpecialty}
              specialtyOptions={activeSpecialtyOptions}
              isSaving={isSavingSpecialty}
            />
            <ListNotes
              isLoading={isLoading}
              allPatients={sortedPatients}
              setPatientsChange={setPatientsChange}
              name={name}
              turnaroundTime={turnaroundTime}
              org={org}
              providerSpecialty={specialty}
              superOrg={superOrg}
              setIsEditing={setIsEditing}
              isEditing={isEditing}
              pickedDate={pickedDate}
              onRefreshSchedule={isStandaloneUser ? null : handleRefreshSchedule}
              isRefreshing={isRefreshing}
              submittingPatientId={submittingPatientId}
              setSubmittingPatientId={setSubmittingPatientId}
              showAddPatientModal={showAddPatientModal}
              setShowAddPatientModal={setShowAddPatientModal}
              noteTitlesEntries={noteTitlesEntries}
              noteTitlesFavorites={noteTitlesFavorites}
              setNoteTitlesFavorites={setNoteTitlesFavorites}
              noteTitleOptionValues={noteTitleOptionValues}
              noteTitleOptionValueSet={noteTitleOptionValueSet}
              firstNoteTitleValue={firstNoteTitleValue}
              isNoteTitlesEnabled={isNoteTitlesEnabled}
            ></ListNotes>
          </div>
        ) : (
          <LoadingScreen />
        )}
      </main>
      <ProviderAttestationModal
        isOpen={shouldShowProviderAttestationGate}
        isLoading={isAttestationGateLoading}
        isSaving={updateProviderAttestationMutation.isPending}
        errorMessage={providerAttestationErrorMessage}
        onRetry={handleProviderAttestationRetry}
        onSubmit={handleProviderAttestationSubmit}
      />
    </>
  );
};

export default Dashboard;
